import { useEffect, useMemo, useState } from 'react';
import {
  Content,
  ContentAccessUser,
  ContentWorkflow,
  UserProfile,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import { useParams } from 'react-router';
import { extractUserAlias } from '../../api/UtilsApi';
import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { EditIneligibilityReason, evaluateEditEligibility } from '../../model/EditEligibility';
import { isLockedBySystem } from '../../model/LoadedDocument';
import { ToolbarAction, disableToolbarActions } from '../../services/ToolbarActionService';
import { useActionPanelState } from '../v2-hooks/useActionPanelState';
import { useBarRaiserEvaluationTab } from '../v2-hooks/useBarRaiserEvaluationTab';
import { useDraftContentLoad } from '../v2-hooks/useDraftContentLoad';
import { useFlushOnClose } from '../v2-hooks/useFlushOnClose';
import { useContent } from '../v2-hooks/useContent';

const EDITOR_TOOLBAR_ACTIONS: readonly ToolbarAction[] = [
  {
    id: 'publication-state',
    icon: 'status-in-progress',
    tooltip: 'Publication state',
    sortOrder: 0,
  },
  { id: 'comments', icon: 'contact', tooltip: 'Comments', sortOrder: 1 },
  { id: 'history', icon: 'history', tooltip: 'History', sortOrder: 2 },
  { id: 'about-stage', icon: 'status-info', tooltip: 'About this stage', sortOrder: 3 },
];

/** Content is still being fetched. */
export interface ContentEditorPageLoadingViewModel {
  readonly status: 'loading';
}

/** Load failed. `errorMessage` is ready to display to the user. */
export interface ContentEditorPageErrorViewModel {
  readonly status: 'error';
  readonly errorMessage: string;
}

/** The content is missing or the viewer is not authorized to see it. */
export interface ContentEditorPageNotFoundViewModel {
  readonly status: 'notFound';
}

/** Load succeeded. All fields are present. */
export interface ContentEditorPageLoadedViewModel {
  readonly status: 'loaded';
  readonly content: Content;
  readonly workflow: ContentWorkflow | undefined;
  /** True when an automated system process holds the content lock. */
  readonly isSystemLocked: boolean;
  readonly createdAt: DateTime | undefined;
  readonly author: UserProfile;
  readonly coAuthors: readonly ContentAccessUser[];
  /** Changes after Accept-All so the page rebuilds every editor from the updated content. */
  readonly editorRemountKey: number;
}

/** The user may not edit this content; the page must redirect to the viewer. */
export interface ContentEditorPageEditForbiddenViewModel {
  readonly status: 'editForbidden';
  readonly reason: EditIneligibilityReason;
  readonly contentId: string;
  /** Alias of the lock holder, when `reason === 'lockedByOther'`; otherwise undefined. */
  readonly lockHolderAlias: string | undefined;
}

export type ContentEditorPageViewModel =
  | ContentEditorPageLoadingViewModel
  | ContentEditorPageErrorViewModel
  | ContentEditorPageNotFoundViewModel
  | ContentEditorPageEditForbiddenViewModel
  | ContentEditorPageLoadedViewModel;

/**
 * Opens `contentId` in edit-draft mode on the {@link ContentEditorService},
 * loads the inline comments for the draft workflow, and binds the page
 * to the service's loaded document. On unmount, both services are closed.
 */
export function useContentEditorPageViewModel(): ContentEditorPageViewModel {
  const {
    loadedDocument: document,
    isLoading,
    errorMessage,
    isNotFound,
  } = useDraftContentLoad('edit');

  const { userId, activeRole } = useCurrentUser();
  const { contentId } = useParams<{ contentId: string }>();
  const content = useContent();
  const qualityCheckService = useQualityCheckService();
  const [editorRemountKey, setEditorRemountKey] = useState(0);

  // Persist in-progress edits if the tab is hidden or closed.
  useFlushOnClose();

  // When the user accepts all quality-check changes, then remount the editors
  // so they show the updated content. Accept-All is rare, so a full rebuild
  // is simpler than patching each editor in place.
  useEffect(() => {
    return qualityCheckService.on('allChangesAccepted', () => {
      setEditorRemountKey((key) => key + 1);
    });
  }, [qualityCheckService]);

  // The bar raiser evaluation tab only appears during the publisher review
  // stage; the hook also prefetches the questionnaire when it applies.
  const workflow = document?.kind === 'draft' ? document.workflow : undefined;
  const barRaiserEvaluationActions = useBarRaiserEvaluationTab(workflow);

  // Until the content has loaded — while loading, or when it is missing or
  // forbidden — the toolbar actions act on content that isn't there, so
  // register them disabled. This also keeps the default panel closed so it
  // doesn't flash open during loading only to close on a 404.
  const toolbarActions = useMemo<readonly ToolbarAction[]>(() => {
    const actions = [...EDITOR_TOOLBAR_ACTIONS, ...barRaiserEvaluationActions];
    return isLoading || isNotFound ? disableToolbarActions(actions) : actions;
  }, [barRaiserEvaluationActions, isLoading, isNotFound]);

  // Declare what tools we need for the toolbar
  useActionPanelState().registerToolbarActions(toolbarActions);

  // Narrow into the discriminated view model.
  // Not-found and error win over loading; loaded only when the handle and the content is set.
  if (isNotFound) return { status: 'notFound' };

  if (errorMessage !== undefined) return { status: 'error', errorMessage };

  if (isLoading || !document || !content) return { status: 'loading' };

  // Enforce edit eligibility now that the real workflow/lock state is known.
  // The viewer's Edit button applies the same rule before navigating here, but
  // direct /edit navigation must be guarded too (P485699101 / P485770037).
  const eligibility = evaluateEditEligibility({
    workflow,
    userId,
    activeRole,
    coAuthors: document.coAuthors,
  });
  if (!eligibility.canEdit) {
    const lockHolderAlias =
      eligibility.reason === 'lockedByOther' && workflow
        ? extractUserAlias(workflow.lock?.acquiredBy ?? '')
        : undefined;
    return {
      status: 'editForbidden',
      reason: eligibility.reason,
      contentId,
      lockHolderAlias,
    };
  }

  return {
    status: 'loaded',
    content: document.content,
    workflow,
    isSystemLocked: workflow ? isLockedBySystem(workflow) : false,
    createdAt: document.kind === 'draft' ? document.createdAt : undefined,
    author: document.author,
    coAuthors: document.coAuthors,
    editorRemountKey,
  };
}
